import React from "react";
import {
  Box,
  Checkbox,
  HStack,
  Image,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  IconButton,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, DownloadIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { getImageURL } from "../../util/getImage";
import { common } from "../../constants/constants";
import { siteInfo } from "../../constants/constants";
import { PaginationFooter } from "../PaginationFooter";

const IMAGE_BASE_PATH = process.env.REACT_APP_DEV_IMAGE_BASE_PATH;
const placeholderImg = `${IMAGE_BASE_PATH}/ui/DummyImage/small.png`;

export const ListView = ({
  loading,
  records,
  columns,
  currentPage,
  totalPages,
  handlePageChange,
  handleEdit,
  selectable,
  selectedRowKeys,
  onRowSelect,
  onSelectAll,
  onRowDelete,
  onRowExport,
  hideActions,
  disableRowClick,
  getRowKey,
  pagination = true,
  getHref,
}) => {
  const navigate = useNavigate();

  const computeRowKey = (record) => {
    if (getRowKey) return getRowKey(record);
    return record?._id || record?.id || record?.alias;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    try {
      return date.toLocaleString("en-IN", {
        timeZone: siteInfo?.INDIANTIMEZONE || "Asia/Kolkata",
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return date.toLocaleString();
    }
  };

  const allRowKeys = Array.isArray(records)
    ? records?.map((r) => computeRowKey(r)).filter(Boolean)
    : [];
  const allSelected =
    selectable &&
    selectedRowKeys &&
    allRowKeys.length > 0 &&
    allRowKeys.every((k) => selectedRowKeys.includes(k));
  const isIndeterminate =
    selectable &&
    selectedRowKeys &&
    selectedRowKeys.length > 0 &&
    !allSelected;

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="sm"
      mt={4}
      position="relative"
      flex="1"
      minH="0"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <TableContainer
        overflowY="auto"
        overflowX="hidden"
        flex="1"
        width="100%"
        pb="20px"
        css={{
          "&::-webkit-scrollbar": {
            width: "3px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#e2e8f0",
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#cbd5e0",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "#e2e8f0 transparent",
        }}
      >
        <Table variant="simple" width="100%" size="sm">
          <Thead
            bg="linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)"
            position="sticky"
            top={0}
            zIndex={2}
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bg: "linear-gradient(90deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%)",
              pointerEvents: "none",
            }}
          >
            <Tr>
              {selectable && (
                <Th width="48px" py={5} px={6} borderBottom="3px solid" borderColor="blue.500">
                  <Checkbox
                    isChecked={allSelected}
                    isIndeterminate={isIndeterminate}
                    onChange={(e) => onSelectAll && onSelectAll(e.target.checked)}
                  />
                </Th>
              )}
              {columns.map((col, index) => (
                <Th
                  key={index}
                  fontSize="13px"
                  fontWeight="800"
                  color="gray.800"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  py={5}
                  px={6}
                  borderBottom="3px solid"
                  borderColor="blue.500"
                >
                  {col.label}
                </Th>
              ))}
              {!disableRowClick && !hideActions && (
                <Th
                  fontSize="13px"
                  fontWeight="800"
                  color="gray.800"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  py={5}
                  px={6}
                  borderBottom="3px solid"
                  borderColor="blue.500"
                  textAlign="center"
                  width="120px"
                >
                  {common.ACTIONS}
                </Th>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={columns.length + (selectable ? 2 : 1)} textAlign="center" py={12}>
                  <VStack spacing={3}>
                    <Spinner size="lg" color="blue.500" thickness="3px" />
                    <Text color="gray.500" fontWeight="500">{common.LOADING_DATA}</Text>
                  </VStack>
                </Td>
              </Tr>
            ) : records?.length > 0 ? (
              records.map((record, rowIndex) => {
                const rowHref = getHref ? getHref(record) : undefined;

                // When a rowHref is available, each data cell gets a stretched
                // anchor overlay so native open-in-new-tab works (right-click
                // menu, Ctrl/Cmd+click, middle click) — mirroring GridCard's
                // approach. A plain left-click is intercepted and routed
                // through SPA navigation; modified/middle clicks fall through
                // to the browser's default new-tab/window behaviour.
                const handleAnchorClick = (e) => {
                  if (e.defaultPrevented) return;
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                  e.preventDefault();
                  navigate(rowHref);
                };

                return (
                <Tr
                  key={rowIndex}
                  onClick={!disableRowClick && !rowHref ? () => handleEdit(record) : undefined}
                  _hover={{
                    cursor: "pointer",
                    bg: "blue.50",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  }}
                  transition="all 0.2s ease"
                >
                  {selectable && (
                    <Td py={4} px={6} width="48px" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      isChecked={selectedRowKeys?.includes(computeRowKey(record))}
                      onChange={(e) => onRowSelect && onRowSelect(computeRowKey(record), e.target.checked, record)}
                    />
                  </Td>
                  )}
                  {columns.map((col, colIndex) => (
                    <Td key={colIndex} position="relative" fontSize="14px" py={4} px={6} whiteSpace="normal">
                      {rowHref && !disableRowClick && (
                        <Box
                          as="a"
                          href={rowHref}
                          onClick={handleAnchorClick}
                          position="absolute"
                          inset={0}
                          zIndex={0}
                          data-allow-context-menu="true"
                        />
                      )}
                      <Box position="relative" zIndex={1}>
                      {col.render
                        ? col.render(record[col.accessor], record)
                        : (col.accessor === "direct_url_of_preview_image" || col.accessor === "direct_url_of_cover_image") && record[col.accessor]
                        ? (
                          <Image
                            src={getImageURL(record[col.accessor], "200", "100")}
                            width="64px"
                            height="64px"
                            objectFit="cover"
                            fallbackSrc={placeholderImg}
                            borderRadius="lg"
                          />
                        )
                        : (col.accessor === "startDate" || col.accessor === "endDate") && record[col.accessor]
                        ? formatDateTime(record[col.accessor])
                        : col.accessor === "isVisible"
                        ? (
                          <Box
                            display="inline-flex"
                            px={2}
                            py={1}
                            borderRadius="full"
                            fontSize="xs"
                            fontWeight="600"
                            bg={record[col.accessor] ? "green.100" : "red.100"}
                            color={record[col.accessor] ? "green.800" : "red.800"}
                          >
                            {record[col.accessor] ? "Yes" : "No"}
                          </Box>
                        )
                        : record[col.accessor] || <Text color="gray.400" fontStyle="italic">—</Text>
                      }
                      </Box>
                    </Td>
                  ))}
                  {!disableRowClick && !hideActions && (
                    <Td py={4} px={6} textAlign="center">
                      <HStack spacing={2} justifyContent="center">
                        <IconButton
                          aria-label="Edit"
                          icon={<EditIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="green"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(record);
                          }}
                        />
                        {onRowExport && (
                          <IconButton
                            aria-label="Export"
                            icon={<DownloadIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRowExport(record);
                            }}
                          />
                        )}
                        {onRowDelete && (
                          <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRowDelete(computeRowKey(record), record);
                            }}
                          />
                        )}
                      </HStack>
                    </Td>
                  )}
                </Tr>
                );
              })
            ) : (
              <Tr>
                <Td colSpan={columns.length + (selectable ? 2 : 1)} textAlign="center" py={12}>
                  <Text fontWeight="600">{common.NO_DATA}</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {pagination && (
        <PaginationFooter currentPage={currentPage} totalPages={totalPages} handlePageChange={handlePageChange} />
      )}
    </Box>
  );
};
